import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Search, Loader2 } from "lucide-react";

// Lightweight beacon for the admin location-pipeline monitoring widget.
// Fire-and-forget POST — never throws, never blocks the autocomplete flow.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const emitGeocodingEvent = (event, extra = {}) => {
  if (!BACKEND_URL) return;
  try {
    const payload = {
      event,
      current_path: typeof window !== "undefined" ? window.location.pathname : "",
      ...extra,
    };
    // Use sendBeacon when available — survives page-navigations and is
    // explicitly fire-and-forget. Fallback to fetch with keepalive so
    // even an in-flight beacon on unload still completes.
    const url = `${BACKEND_URL}/api/analytics/geocoding-event`;
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* never let telemetry break UX */ }
};

// ─── GEOCODER CONFIG ────────────────────────────────────────────────────────
// REACT_APP_GEOCODER controls which backend serves autocomplete suggestions:
//   - "google" (preferred):  Google Places Autocomplete (New) — best US
//                            address coverage, especially for off-MLS and
//                            older properties. Same vendor users see on
//                            Zillow/Realtor so muscle memory matches.
//   - "mapbox" (fallback):   Mapbox Geocoding — works fine for city/ZIP
//                            and most pasted addresses post-paste-fix,
//                            but coverage is thinner on edge addresses.
// If REACT_APP_GEOCODER is unset, we pick whichever vendor has a key set.
// If both are set, Google wins. If neither is set, the input is plain text.
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const GEOCODER_PREF = (process.env.REACT_APP_GEOCODER || "").toLowerCase();
const ACTIVE_GEOCODER =
  GEOCODER_PREF === "mapbox" && MAPBOX_TOKEN
    ? "mapbox"
    : GOOGLE_KEY
    ? "google"
    : MAPBOX_TOKEN
    ? "mapbox"
    : null;

const LocationAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search any U.S. city, county, state, or ZIP...",
  className = "",
  inputClassName = "",
  showIcon = true,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  // Google Places "session token" — used to bundle a sequence of
  // autocomplete calls into a single billing session. Without this,
  // every keystroke is billed as a new session ($2.83/1K vs $0 per
  // autocomplete call within a session). We rotate the token after
  // each `handleSelect` so the next typing session starts fresh.
  const sessionTokenRef = useRef(null);
  const getSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Math.random()).slice(2);
    }
    return sessionTokenRef.current;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ─── GOOGLE PLACES AUTOCOMPLETE (NEW) ────────────────────────────────────
  // POST https://places.googleapis.com/v1/places:autocomplete
  // Docs: https://developers.google.com/maps/documentation/places/web-service/place-autocomplete
  // Returns mixed predictions (addresses + cities + ZIPs + regions) ranked
  // by Google's relevance model. We pass `includedRegionCodes: ["us"]` so
  // we only see US results. No need for paste-detection heuristics —
  // Google's autocomplete handles fully-typed/pasted addresses natively.
  const fetchGoogleSuggestions = async (query, signal) => {
    const url = "https://places.googleapis.com/v1/places:autocomplete";
    const body = {
      input: query,
      includedRegionCodes: ["us"],
      sessionToken: getSessionToken(),
    };
    const t0 = performance.now();
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": GOOGLE_KEY,
        "X-Goog-FieldMask":
          "suggestions.placePrediction.placeId," +
          "suggestions.placePrediction.text," +
          "suggestions.placePrediction.structuredFormat," +
          "suggestions.placePrediction.types",
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) {
      emitGeocodingEvent("geocoding_error", {
        vendor: "google",
        status_code: res.status,
        query_len: query.length,
        duration_ms: Math.round(performance.now() - t0),
      });
      throw new Error(`Google Places ${res.status}`);
    }
    const data = await res.json();
    const preds = data.suggestions || [];

    const results = preds
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .map((p) => {
        const types = p.types || [];
        // Map Google's place types to our internal vocab
        let kind = "place";
        if (types.includes("street_address") || types.includes("premise") || types.includes("subpremise") || types.includes("route")) {
          kind = "address";
        } else if (types.includes("postal_code")) {
          kind = "postcode";
        } else if (types.includes("administrative_area_level_1")) {
          kind = "region";
        } else if (types.includes("administrative_area_level_2")) {
          kind = "district";
        } else if (types.includes("locality") || types.includes("sublocality")) {
          kind = "place";
        }

        // Google gives `structuredFormat.mainText` (e.g. "2563 13th Ave S")
        // and `secondaryText` (e.g. "Saint Petersburg, FL"). Combine for
        // the same "short label" experience we had on Mapbox.
        const main = p.structuredFormat?.mainText?.text || "";
        const secondary = p.structuredFormat?.secondaryText?.text || "";
        const fullText = p.text?.text || `${main}${secondary ? ", " + secondary : ""}`;
        // Strip trailing ", USA" / ", United States" — Mapbox-style cleanup
        const clean = fullText
          .replace(/,\s*(USA|United States)$/i, "")
          .trim();

        return {
          label: fullText,
          short: clean,
          coordinates: null, // not requested here; uses text-based backend search
          type: kind,
          placeId: p.placeId,
        };
      });
    emitGeocodingEvent("geocoding_call", {
      vendor: "google",
      result_count: results.length,
      query_len: query.length,
      duration_ms: Math.round(performance.now() - t0),
    });
    return results;
  };

  // ─── MAPBOX FALLBACK (with paste detection cascade) ──────────────────────
  // Kept for the case where Google's key is unset, or as an emergency
  // toggle via REACT_APP_GEOCODER=mapbox. Same cascade as before — paste
  // detection, ZIP-strip, Saint↔St normalization, autocomplete fallback.
  const fetchMapboxSuggestions = async (query, signal) => {
    const trimmed = query.trim();
    const looksLikeFullAddress =
      /^\d+\s/.test(trimmed) && trimmed.includes(",");

    const buildUrl = (q, opts = {}) => {
      const { autocomplete = true, addressOnly = false } = opts;
      const types = addressOnly
        ? "address"
        : "address,place,postcode,region,district,locality";
      return (
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json` +
        `?access_token=${MAPBOX_TOKEN}` +
        `&types=${types}` +
        `&country=us` +
        `&limit=7` +
        `&autocomplete=${autocomplete}`
      );
    };

    const attempts = looksLikeFullAddress
      ? [
          { q: trimmed, autocomplete: false, addressOnly: true },
          { q: trimmed.replace(/\s+\d{5}(?:-\d{4})?$/, "").trim(), autocomplete: false, addressOnly: true },
          { q: trimmed.replace(/\bSaint\s/gi, "St ").replace(/\bSt\.\s/gi, "St "), autocomplete: false, addressOnly: true },
          { q: trimmed, autocomplete: true, addressOnly: false },
        ]
      : [{ q: trimmed, autocomplete: true, addressOnly: false }];

    const seen = new Set();
    let features = [];
    for (const a of attempts) {
      if (!a.q || seen.has(a.q)) continue;
      seen.add(a.q);
      const url = buildUrl(a.q, { autocomplete: a.autocomplete, addressOnly: a.addressOnly });
      try {
        const res = await fetch(url, { signal });
        if (!res.ok) continue;
        const data = await res.json();
        features = data.features || [];
        if (features.length > 0) break;
      } catch (err) {
        if (err.name === "AbortError") throw err;
      }
    }

    return features.map((f) => {
      const context = f.context || [];
      const region = context.find((c) => c.id?.startsWith("region"));
      const regionShort = region?.short_code?.replace("US-", "") || region?.text || "";
      let cleanLabel;
      if (f.place_type?.[0] === "address") {
        cleanLabel = f.place_name?.replace(/, United States$/, "") || f.text;
      } else if (f.place_type?.[0] === "postcode") {
        cleanLabel = regionShort ? `${f.text}, ${regionShort}` : f.text;
      } else {
        cleanLabel = regionShort ? `${f.text}, ${regionShort}` : f.text;
      }
      return {
        label: f.place_name,
        short: cleanLabel,
        coordinates: f.center,
        type: f.place_type?.[0] || "place",
      };
    });
  };

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2 || !ACTIVE_GEOCODER) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    try {
      let results;
      if (ACTIVE_GEOCODER === "google") {
        results = await fetchGoogleSuggestions(query, abortRef.current.signal);
        // Defensive fallback: if Google returns nothing AND Mapbox is
        // configured, try Mapbox once so users always see SOMETHING for
        // a valid-looking query. Doesn't fire on aborted requests.
        if (results.length === 0 && MAPBOX_TOKEN) {
          emitGeocodingEvent("geocoding_fallback", {
            vendor: "mapbox",
            query_len: query.length,
          });
          try {
            results = await fetchMapboxSuggestions(query, abortRef.current.signal);
          } catch (_) { /* swallow — Google's empty result is the answer */ }
        }
      } else {
        results = await fetchMapboxSuggestions(query, abortRef.current.signal);
      }
      setSuggestions(results);
      setIsOpen(results.length > 0);
      setHighlightedIndex(-1);
    } catch (err) {
      if (err.name !== "AbortError") {
        // Vendor outage / quota exceeded / bad key — clear suggestions
        // rather than show stale data. The error logger will pick up the
        // network failure separately.
        setSuggestions([]);
        setIsOpen(false);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const handleSelect = (suggestion) => {
    const display = suggestion.short || suggestion.label;
    onChange(display);
    setSuggestions([]);
    setIsOpen(false);
    // Rotate the Google session token — pricing optimization (each
    // autocomplete "session" ends when the user selects a suggestion).
    sessionTokenRef.current = null;
    // Fire the select beacon — starts the "select → analysis" conversion
    // clock. Dashboard's search handler emits the matching success/empty
    // event once results land. The window-scoped timestamp lets any
    // downstream consumer (DashboardPage, AnalyzeModal, etc.) know that
    // the most recent location value came from an autocomplete pick.
    if (typeof window !== "undefined") {
      window.__realixLastAutocompleteSelect = Date.now();
    }
    emitGeocodingEvent("geocoding_select", {
      vendor: ACTIVE_GEOCODER,
      place_type: suggestion.type,
    });
    if (onSelect) onSelect(display, suggestion);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightedIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const typeLabel = (type) => {
    const map = { address: "Address", place: "City", postcode: "ZIP", region: "State", district: "County", locality: "Area" };
    return map[type] || "Place";
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid="location-autocomplete">
      <div className="relative">
        {showIcon && (
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
            ) : (
              <Search className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </div>
        )}
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className={`w-full bg-secondary/50 border border-border rounded-md text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-emerald-500/40 ${showIcon ? "pl-8 pr-3" : "px-3"} py-2 ${inputClassName}`}
          data-testid="location-autocomplete-input"
          autoComplete="off"
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-xl overflow-hidden max-h-[280px] overflow-y-auto" data-testid="location-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={s.placeId || i}
              type="button"
              onClick={() => handleSelect(s)}
              onMouseEnter={() => setHighlightedIndex(i)}
              className={`w-full px-3 py-2.5 text-left flex items-center gap-2.5 text-sm transition-colors ${
                i === highlightedIndex ? "bg-emerald-500/10 text-foreground" : "text-muted-foreground hover:bg-secondary/50"
              }`}
              data-testid={`location-suggestion-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />
              <div className="flex-1 min-w-0">
                <span className="block truncate font-medium text-foreground text-sm">{s.short}</span>
                <span className="block truncate text-xs text-muted-foreground">{s.label}</span>
              </div>
              <span className="text-[10px] font-medium text-muted-foreground bg-secondary/80 px-1.5 py-0.5 rounded-full flex-shrink-0">
                {typeLabel(s.type)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
